import {
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';
import { UserPlanType } from 'features/users/users.types';
import { useTranslation } from 'react-i18next';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Link } from 'react-router-dom';
import { useOrganizationMembers } from 'features/users/hooks/useOrganizationMembers';
import { useAuthState } from 'state/useAuthState';
import { SIMPLE_PAGE_LAYOUT_ID } from 'components/layouts/SimplePageLayout';

const planTypeToLabelTransKey: Record<UserPlanType, string> = {
  regular: 'organization.member',
  pro: 'organization.proMember',
};

const LoadingSpinner = (): JSX.Element[] => {
  return Array.from({ length: 5 }).map((_, idx) => (
    <Skeleton
      // eslint-disable-next-line react/no-array-index-key
      key={idx}
      variant="rounded"
      sx={{ marginBottom: 3, marginX: 2 }}
    />
  ));
};

export const OrganizationMembers = (): JSX.Element | null => {
  const { t } = useTranslation();
  const {
    permissions: { canManageOrganization },
  } = useAuthState();
  const { fetchNextPage, hasNextPage, members, isLoading } =
    useOrganizationMembers({ enabled: canManageOrganization });

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (!members.length) {
    return null;
  }

  return (
    <>
      <Typography
        variant="h3"
        sx={{ fontWeight: 'fontWeightBold', px: 2, py: 1 }}
      >
        {t('organization.people')}
      </Typography>

      <InfiniteScroll
        dataLength={members.length}
        next={fetchNextPage}
        hasMore={Boolean(hasNextPage)}
        loader={<LoadingSpinner />}
        style={{ overflow: 'inherit' }}
        scrollableTarget={SIMPLE_PAGE_LAYOUT_ID}
      >
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography
                    variant="h4"
                    sx={(theme) => ({
                      fontWeight: theme.typography.fontWeightBold,
                      fontSize: theme.typography.body2.fontSize,
                    })}
                  >
                    {t('organization.name')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="h4"
                    sx={(theme) => ({
                      fontWeight: theme.typography.fontWeightBold,
                      fontSize: theme.typography.body2.fontSize,
                    })}
                  >
                    {t('organization.status')}
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {members.map((member) => (
                <TableRow key={member.id} hover>
                  <TableCell>
                    <Stack
                      component={Link}
                      to={`/organization/members/${member.id}`}
                      sx={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Typography variant="body2">
                        {member.firstName} {member.lastName}
                      </Typography>
                      <Typography variant="subtitle1" color="secondary">
                        {member.email}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      component={Link}
                      to={`/organization/members/${member.id}`}
                      sx={{ textDecoration: 'none', color: 'inherit' }}
                    >
                      <Typography variant="subtitle1">
                        {member.planType
                          ? t(planTypeToLabelTransKey[member.planType])
                          : '-'}
                      </Typography>
                      <KeyboardArrowRightIcon />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </InfiniteScroll>
    </>
  );
};
